import { useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import { toast } from '@/hooks/useToast'

import useRemoveCourse from '../mutations/useRemoveCourse'
import type { SimpleCourse } from '../types/simple-course'

type RemoveCourseModalProps = {
  course: SimpleCourse
  onOpenChange: (course: SimpleCourse | null) => void
  onRemoveSuccess?: (course: SimpleCourse) => void
}

const RemoveCourseModal = (props: RemoveCourseModalProps) => {
  const { t } = useTranslation('coursesManagement')

  const { mutate: removeCourse, isPending } = useRemoveCourse({
    onSuccess: () => {
      props.onRemoveSuccess?.(props.course)
      toast({
        variant: 'success',
        title: t('toast.course-remove-success', {
          COURSE: props.course.displayName
        })
      })
      props.onOpenChange(null)
    },
    onError: () => {
      toast({
        variant: 'error',
        title: t('toast.course-remove-error', {
          COURSE: props.course.displayName
        })
      })
    }
  })

  const handleOpenChange = (value: boolean) =>
    props.onOpenChange(value ? props.course : null)

  const handleConfirmClick = () => removeCourse(props.course.id)

  return (
    <ConfirmModal
      header={t('header.remove-course')}
      subheader={
        <>
          <p>{t('text.want-to-remove-course')}</p>
          <p>{t('text.can-not-be-undone')}</p>
        </>
      }
      confirmButton={
        <Button
          variant="danger"
          onClick={handleConfirmClick}
          loading={isPending}
        >
          {t('button.remove')}
        </Button>
      }
      open
      onOpenChange={handleOpenChange}
    />
  )
}

export default RemoveCourseModal
